// AI Step 5: Controls & Mitigations
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Zap, Eye, Shield } from 'lucide-react';
import { TECHNICAL_CONTROLS, ORGANISATIONAL_CONTROLS, DATA_PROTECTION_CONTROLS } from '../constants';

export default function Step5Controls({ formData, updateField }) {
  const toggleControl = (type, code) => {
    const field = `${type}_controls`;
    const current = formData[field] || [];
    const existing = current.find(c => c.code === code);

    let updated;
    if (existing) {
      updated = current.map(c => c.code === code ? { ...c, implemented: !c.implemented } : c);
    } else {
      const controlList = type === 'technical' ? TECHNICAL_CONTROLS : type === 'organisational' ? ORGANISATIONAL_CONTROLS : DATA_PROTECTION_CONTROLS;
      const control = controlList.find(c => c.code === code);
      updated = [...current, { id: code, category: type, code, description: control.description, implemented: true }];
    }
    updateField(field, updated);
  };

  const isImplemented = (type, code) => {
    const field = `${type}_controls`;
    const control = (formData[field] || []).find(c => c.code === code);
    return control?.implemented || false;
  };

  const techCount = (formData.technical_controls || []).filter(c => c.implemented).length;
  const orgCount = (formData.organisational_controls || []).filter(c => c.implemented).length;
  const dpCount = (formData.data_protection_controls || []).filter(c => c.implemented).length;

  const renderControlSection = (title, Icon, iconColor, type, controls, count) => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          {title}
          <Badge variant="secondary" className="ml-auto">{count}/{controls.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-2">
          {controls.map(control => (
            <label key={control.code} className="flex items-start gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
              <Checkbox checked={isImplemented(type, control.code)} onCheckedChange={() => toggleControl(type, control.code)} className="mt-0.5" />
              <div>
                <span className="text-xs font-mono text-muted-foreground">{control.code}</span>
                <p className="text-sm">{control.description}</p>
              </div>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Controls & Mitigations</h2>
        <p className="text-muted-foreground mt-1">Document the controls in place (NIST MANAGE function).</p>
      </div>

      {renderControlSection('Technical Controls', Zap, 'text-blue-500', 'technical', TECHNICAL_CONTROLS, techCount)}
      {renderControlSection('Organisational Controls', Eye, 'text-purple-500', 'organisational', ORGANISATIONAL_CONTROLS, orgCount)}
      {renderControlSection('Data Protection Controls', Shield, 'text-green-500', 'data_protection', DATA_PROTECTION_CONTROLS, dpCount)}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Additional Controls</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Human Oversight Details</Label>
            <Textarea value={formData.human_oversight_details || ''} onChange={e => updateField('human_oversight_details', e.target.value)} placeholder="Describe human oversight mechanisms for AI decisions..." className="mt-1.5" rows={3} />
          </div>
          <div>
            <Label>Monitoring Approach</Label>
            <Textarea value={formData.monitoring_approach || ''} onChange={e => updateField('monitoring_approach', e.target.value)} placeholder="How is the AI system monitored for performance, bias, and drift?" className="mt-1.5" rows={3} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
